import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, RefreshCw, AlertTriangle, CheckCircle2, Lightbulb, Clock } from 'lucide-react';
import { dbOptApi } from '../services/api';
import { Card, SectionHeader, StatCard, Loader, Button, Badge, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';

function ms(n) {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${Number(n).toFixed(1)}ms`;
}

function pct(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function usd(n) {
  if (n == null || n === 0) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(2)}K`;
  return `$${Number(n).toFixed(2)}`;
}

export default function DatabaseOptimizationPage() {
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState(null);

  const { data: customers = [], isLoading: loadingCustomers, isError: customersError } = useQuery({
    queryKey: ['db-customers'],
    queryFn: () => dbOptApi.listCustomers().then((r) => r.data),
    retry: 1,
  });

  useEffect(() => {
    if (Array.isArray(customers) && customers.length > 0 && !customerId) {
      const first = customers[0];
      setCustomerId(first.id || first.customerId || first);
    }
  }, [customers, customerId]);

  const enabled = !!customerId;

  const { data: metrics, isLoading: loadingMetrics, refetch: refetchMetrics } = useQuery({
    queryKey: ['db-metrics', customerId],
    queryFn: () => dbOptApi.getMetrics(customerId).then((r) => r.data),
    enabled,
  });

  const { data: suggestions } = useQuery({
    queryKey: ['db-suggestions', customerId],
    queryFn: () => dbOptApi.getSuggestions(customerId).then((r) => r.data),
    enabled,
  });

  const refreshMut = useMutation({
    mutationFn: () => dbOptApi.refresh(customerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['db-metrics', customerId] });
      qc.invalidateQueries({ queryKey: ['db-suggestions', customerId] });
      toast.success('Metrics refreshed');
    },
    onError: () => toast.error('Refresh failed — is sb-database running on 8081?'),
  });

  if (customersError) {
    return (
      <div>
        <SectionHeader title="Database Optimization" subtitle="PostgreSQL health, slow queries, and optimization suggestions" />
        <Card style={{ padding: 24 }}>
          <EmptyState
            message="Could not reach the database metrics service. Ensure sb-database is running on http://localhost:8081."
            icon={AlertTriangle}
          />
        </Card>
      </div>
    );
  }

  if (loadingCustomers) return <Loader />;

  const pool = metrics?.connectionPool;
  const qp = metrics?.queryPerformance;
  const slow = metrics?.topSlowQueries || [];
  const tables = metrics?.tableHealth || [];
  const cache = metrics?.cacheStats;
  const repl = metrics?.replicationStatus;
  const unusedIdx = metrics?.unusedIndexes || [];

  return (
    <div>
      <SectionHeader
        title="Database Optimization"
        subtitle="PostgreSQL health, slow queries, and optimization suggestions (via sb-database)"
        actions={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {customers.length > 0 && (
              <select
                value={customerId || ''}
                onChange={(e) => setCustomerId(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                {customers.map((c, i) => {
                  const id = c.id || c.customerId || c;
                  const label = c.displayName || c.name || id;
                  return <option key={id || i} value={id}>{label}</option>;
                })}
              </select>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => refreshMut.mutate()}
              disabled={!enabled || refreshMut.isPending}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <RefreshCw size={14} style={{ animation: refreshMut.isPending ? 'spin 1s linear infinite' : 'none' }} />
              {refreshMut.isPending ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        }
      />

      {!customers.length && (
        <Card style={{ padding: 24 }}>
          <EmptyState message="No customer databases configured. Add entries under app.customers in sb-database/application.yml." />
        </Card>
      )}

      {enabled && loadingMetrics && <Loader />}

      {enabled && !loadingMetrics && metrics && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard
              icon={Database}
              label="Connection pool"
              value={pool ? `${pool.activeConnections}/${pool.maxConnections}` : '—'}
              subtext={pool ? `${pct(pool.utilizationPercent)} utilisation` : ''}
            />
            <StatCard
              icon={Clock}
              label="Avg query latency"
              value={qp ? ms(qp.avgLatencyMs) : '—'}
              subtext={qp ? `p95 ${ms(qp.p95LatencyMs)} · p99 ${ms(qp.p99LatencyMs)}` : ''}
            />
            <StatCard
              icon={CheckCircle2}
              label="Cache hit ratio"
              value={cache ? pct(cache.cacheHitRatio) : '—'}
              subtext={cache?.status || ''}
            />
            <StatCard
              icon={AlertTriangle}
              label="Slow queries"
              value={slow.length}
              subtext={`${unusedIdx.length} unused indexes`}
            />
          </div>

          {/* Slow queries */}
          <Card style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 14 }}>
              Top slow queries
            </div>
            {slow.length === 0 ? (
              <EmptyState message="No slow queries recorded." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {slow.map((q, i) => (
                  <div key={q.queryHash || i} style={{
                    padding: 14,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                          {q.tableName && <Badge label={q.tableName} type="PENDING" />}
                          <Badge label={`${q.executionCount || 0} runs`} type="MEDIUM" />
                          {q.estimatedCostPerDayUsd > 0 && (
                            <Badge label={`${usd(q.estimatedCostPerDayUsd)}/day`} type="HIGH" />
                          )}
                        </div>
                        <pre style={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: 12,
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.5,
                          margin: 0,
                          marginBottom: 8,
                        }}>
                          {q.queryText}
                        </pre>
                        {q.suggestion && (
                          <div style={{
                            color: '#4a9eff',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 6,
                            lineHeight: 1.5,
                          }}>
                            <Lightbulb size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                            <span>{q.suggestion}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ color: '#ff9900', fontSize: 20, fontWeight: 700 }}>
                          {ms(q.avgDurationMs)}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>avg duration</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Two-column section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* Table health */}
            <Card style={{ padding: 20 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Table health</div>
              {tables.length === 0 ? (
                <EmptyState message="No table bloat detected." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tables.slice(0, 8).map((t, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 10px',
                      background: 'rgba(255,255,255,0.02)',
                      borderRadius: 8,
                    }}>
                      <div>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t.tableName}</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                          {t.liveTuples?.toLocaleString() || 0} live · {t.deadTuples?.toLocaleString() || 0} dead
                        </div>
                      </div>
                      <div style={{
                        color: t.deadTuplePercent > 20 ? '#ff5c5c' : t.deadTuplePercent > 10 ? '#ffb800' : '#00c896',
                        fontSize: 14,
                        fontWeight: 700,
                      }}>
                        {pct(t.deadTuplePercent)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Replication */}
            <Card style={{ padding: 20 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Replication status</div>
              {!repl ? (
                <EmptyState message="Replication data unavailable." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Row label="Replicating" value={repl.replicating ? 'Yes' : 'No'} good={repl.replicating} />
                  <Row label="Lag" value={`${repl.lagSeconds}s`} good={repl.lagSeconds < 1} />
                  <Row label="Primary" value={repl.primaryHost || '—'} />
                  <Row label="Replica" value={repl.replicaHost || '—'} />
                  <Row label="Bytes behind" value={(repl.bytesBehind || 0).toLocaleString()} />
                </div>
              )}
            </Card>
          </div>

          {/* Suggestions */}
          {suggestions && (
            <Card style={{ padding: 20 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Lightbulb size={16} color="#ffb800" /> Optimization recommendations
              </div>
              <SuggestionGroup title="Indexes" items={suggestions.indexRecommendations} />
              <SuggestionGroup title="Vacuum" items={suggestions.vacuumRecommendations} />
              <SuggestionGroup title="Partitioning" items={suggestions.partitioningRecommendations} />
              <SuggestionGroup title="Configuration" items={suggestions.configurationRecommendations} />
              <SuggestionGroup title="Security" items={suggestions.securityRecommendations} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Row({ label, value, good }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'rgba(255,255,255,0.5)' }}>{label}</span>
      <span style={{ color: good === undefined ? '#fff' : good ? '#00c896' : '#ff5c5c', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function SuggestionGroup({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 1.7 }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
